<template>
  <div class="detail-container">
    <div class="detail-header flex items-center px-4 py-2">
      <img
        src="@/assets/gc-bi/icon/svg/ic24-arrow-left.svg"
        alt="list-icon"
        class="back-icon w-4"
        @click="handleBack"
      />
      <div class="ml-2 detail-title">{{ currentTaskName }}</div>
      <div v-if="userName" class="user-name">{{ userName }}</div>
    </div>
    <div v-if="loading" class="flex justify-center py-5">
      <van-loading type="spinner" />
    </div>
    <div v-else class="content-wrapper">
      <van-tabs
        v-model:active="active"
        color="#F59B22"
        title-active-color="#F59B22"
        @change="onTabChange"
      >
        <van-tab title="Detail" name="detail">
          <DetailPage ref="detailRef" v-model:detail-data="doorDetail" @refresh="fetchDetail" />
        </van-tab>
        <van-tab
          v-if="!isApprovalsClosure && !isDoorSummaryClosure"
          title="Attachment"
          name="attachment"
        >
          <AttachmentPage :detail-data="doorDetail" :no-gate="noGate" />
        </van-tab>
        <van-tab v-if="!isApprovalsClosure && !isDoorSummaryClosure" title="Picture" name="picture">
          <PicturePage :detail-data="doorDetail" />
        </van-tab>
        <van-tab v-if="!isDoorSummaryView" title="Comment" name="comment">
          <CommentPage ref="commentRef" :detail-data="doorDetail" />
        </van-tab>
        <van-tab title="History" name="history">
          <HistoryPage :detail-data="doorDetail" />
        </van-tab>
      </van-tabs>
    </div>

    <div v-if="kiwiVisible || displayedGateActions.length" class="footer-actions px-4 py-2 flex gap-4">
      <van-button v-if="kiwiVisible" block color="#000" class="kiwi-btn" @click="goKiwi">KIWI</van-button>
      <van-button
        v-for="btn in primaryActions"
        :key="btn"
        block
        :color="actionColorConfig[btn]"
        @click="handleAction(btn)"
      >
        {{ btn }}
      </van-button>
      <van-button
        v-if="moreActions.length"
        color="#000"
        class="more-btn"
        @click="moreVisible = true"
      >
        <div class="more-content">
          <img src="@/assets/gc-bi/icon/svg/ic24-more-hor.svg" class="more-icon" />
          <div class="more-text">More</div>
        </div>
      </van-button>
    </div>

    <van-action-sheet
      v-model:show="moreVisible"
      :actions="moreActionItems"
      close-on-click-action
      @select="onMoreSelect"
    />

    <GlobalDialog
      v-model:show="returnDialogVisible"
      :confirm1Text="returnConfirm1Text"
      :confirm2Text="returnConfirm2Text"
      @confirm1="onReturnConfirm1"
      @confirm2="onReturnConfirm2"
      @cancel="returnDialogVisible = false"
    />

    <GlobalDialog
      v-model:show="confirmDialogVisible"
      @confirm1="onConfirmAction"
      @cancel="confirmDialogVisible = false"
    />

    <van-dialog
      v-model:show="showClosureEdit"
      :style="{
        maxHeight: 'calc(100vh - 250px)',
        overflowY: 'auto',
        width: '92vw',
        maxWidth: '680px',
      }"
      title="Closure Information"
      :show-cancel-button="false"
      :show-confirm-button="false"
    >
      <div class="edit-form">
        <div class="edit-row">
          <div class="edit-label">Closure Date</div>
          <input type="date" class="edit-input" v-model="closureForm.closureDate" />
        </div>
        <div class="edit-row">
          <div class="edit-label">
            <span v-if="isClosureReasonRequired" class="req">*</span>Closure Reason
          </div>
          <van-field
            v-model="closureForm.closureReasonText"
            is-link
            arrow-direction="down"
            readonly
            :placeholder="'please select'"
            @click="openClosureSelect()"
          />
        </div>
        <div class="edit-row col-span-2">
          <div class="edit-label">
            <span v-if="isAECommentRequired" class="req">*</span>AE Comment
          </div>
          <textarea class="edit-textarea" v-model="closureForm.aeComment" />
        </div>
      </div>
      <div class="dialog-footer-fixed">
        <van-button plain class="dialog-cancel" @click="showClosureEdit = false">Cancel</van-button>
        <van-button type="primary" class="dialog-confirm" @click="onClosureConfirm"
          >Confirm</van-button
        >
      </div>
    </van-dialog>

    <van-popup v-model:show="closureSelectVisible" position="bottom" round>
      <van-picker
        :columns="closureReasonOptions"
        @confirm="onClosureSelectConfirm"
        @cancel="closureSelectVisible = false"
      />
    </van-popup>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { useRoute, useRouter, onBeforeRouteLeave } from 'vue-router'
import { showToast } from 'vant'
import { withLoading, showToastNextTick } from '@/utils/request'
import { getClcDoor, getDoorObserverClcDoor, getClcDoorMasterKiwiUrl } from '@/api/detail'
import { getClosureRuleOptions, getClosureTodo, doTaskClosure } from '@/api/closure'
import { doTask, pendingTask, dropTask, saveAndGetData } from '@/api/approvals'
import { getComments } from '@/api/comment'
import { getAttachments } from '@/api/attachment'
import DetailPage from './Detail.vue'
import AttachmentPage from './Attachment.vue'
import PicturePage from './Picture.vue'
import CommentPage from './Comment.vue'
import HistoryPage from './History.vue'
import {
  approveButtonConfig,
  actionColorConfig,
  ApprovalAction,
  lastGateConfig,
  approvalsTab,
  kiwiConfig,
} from '@/utils/config'
import { detailEditConfig, type EditConfigItem } from '@/utils/detail-config'
import { requiredAttachmentConfig, docTypeList } from '@/utils/attachment-config'
import GlobalDialog from '@/components/GlobalDialog.vue'

const route = useRoute()
const router = useRouter()
const active = ref(String(route.query.tab || 'detail'))
const currentTaskName = computed(() => String(route.query.currentTaskName || ''))

const doorDetail = ref<any>(null)
const loading = ref(false)

const clcDoorCode = computed(() => String(route.query.clcDoorCode || ''))
const taskId = computed(() => String(route.query.taskId || ''))
const storeNameLocal = computed(() => String(route.query.storeNameLocal || ''))
const clcProjectCode = computed(() => String(route.query.clcProjectCode || ''))
const batchId = computed(() => String(route.query.batchId || ''))
const indexFromDoorSummary = computed(() => Number(route.query.tabIndex || ''))

const commentRef = ref()
const detailRef = ref()

// door Summary页面进入，且无节点配置，特殊处理attachment展示
const noGate = computed(() => isDoorSummaryView.value && [3, 4, 6, 7].includes(indexFromDoorSummary.value))

const onTabChange = (name: string) => {
  if (name === 'comment') {
    commentRef.value?.fetchComments()
  }
}

const pendingTaskWithHandle = async (data: { doorCode: string; taskId: string }[]) => {
  try {
    return await pendingTask(data)
  } catch (error: any) {
    const code = Number(error?.code)
    if (code === 203) {
      active.value = 'attachment'
      router.replace({
        path: '/approvalsDetail',
        query: { ...(route.query as any), tab: 'attachment' },
      })
      return false
    }
    if (code === 10021) {
      active.value = 'comment'
      router.replace({
        path: '/approvalsDetail',
        query: { ...(route.query as any), tab: 'comment' },
      })
      return false
    }
    if (code === 401) {
      return false
    }
    active.value = 'detail'
    router.replace({ path: '/approvalsDetail', query: { ...(route.query as any), tab: 'detail' } })
    detailRef.value?.openEdit?.()
    return false
  }
}

const dropTaskWithHandle = async (data: string[]) => {
  try {
    return await dropTask(data)
  } catch (error: any) {
    const code = Number(error?.code)
    if (code === 203) {
      active.value = 'attachment'
      router.replace({
        path: '/approvalsDetail',
        query: { ...(route.query as any), tab: 'attachment' },
      })
      return false
    }
    if (code === 10021) {
      active.value = 'comment'
      router.replace({
        path: '/approvalsDetail',
        query: { ...(route.query as any), tab: 'comment' },
      })
      return false
    }
    if (code === 401) {
      return false
    }
    active.value = 'detail'
    router.replace({ path: '/approvalsDetail', query: { ...(route.query as any), tab: 'detail' } })
    detailRef.value?.openEdit?.()
    return false
  }
}

const gateKey = computed(() => {
  const name = currentTaskName.value
  for (const key of Object.keys(approveButtonConfig)) {
    if (name.startsWith(key)) return key
  }
  return ''
})

const gateActions = computed<ApprovalAction[]>(() => {
  return gateKey.value ? (approveButtonConfig[gateKey.value] ?? []) : []
})

// 只在从 Door Summary 页面进入时隐藏所有审批按钮
const isDoorSummaryView = computed(() => String(route.query.fromDoorSummary || '') === '1')
const displayedGateActions = computed<ApprovalAction[]>(() => {
  if (isDoorSummaryView.value) return []
  return gateActions.value
})

const handleBack = () => {
  router.push(isDoorSummaryView.value ? '/history' : '/approvals')
}

const kiwiVisible = computed(() => kiwiConfig.includes(gateKey.value) && !isDoorSummaryView.value)

const primaryActions = computed<ApprovalAction[]>(() => {
  const actions = displayedGateActions.value
  const total = actions.length + (kiwiVisible.value ? 1 : 0)
  if (total > 4) {
    const limit = 4 - 1 - (kiwiVisible.value ? 1 : 0)
    return actions.slice(0, Math.max(0, limit))
  }
  return actions
})

const goKiwi = async () => {
  const code = clcDoorCode.value
  if (!code) {
    showToast('Missing door code')
    return
  }
  try {
    const res = await getClcDoorMasterKiwiUrl(code)
    const url = String(res?.data || '').trim()
    if (!url) {
      showToast('Unable to get Kiwi URL')
      return
    }
    location.assign(url)
  } catch (error: any) {
    if (!error?.isToastShown) {
      showToast('Unable to get Kiwi URL')
    }
  }
}


const moreActions = computed<ApprovalAction[]>(() => {
  const actions = displayedGateActions.value
  const total = actions.length + (kiwiVisible.value ? 1 : 0)
  if (total > 4) {
    const limit = 4 - 1 - (kiwiVisible.value ? 1 : 0)
    return actions.slice(limit)
  }
  return []
})

const moreActionItems = computed(() => moreActions.value.map((a) => ({ name: a })))

const moreVisible = ref(false)

const onMoreSelect = (item: any) => {
  const name = String(item?.name || '') as ApprovalAction
  if (name) handleAction(name)
}

const returnDialogVisible = ref(false)
const doorFormat = computed(() => String(doorDetail.value?.format || ''))

const confirmDialogVisible = ref(false)
const confirmDialogAction = ref<ApprovalAction | ''>('')
const userName = ref('')

const isApprovalsClosure = computed(() => {
  const key = String(doorDetail.value?.processDefinitionKey || '')
  return key === 'nike-mpp-closure-door-ae-approval' || key === 'nike-mpp-closure-door-mp-approval'
})

const isDoorSummaryClosure = computed(() => indexFromDoorSummary.value === 3)

const isFWW = computed(() => {
  const k = String(doorDetail.value?.processDefinitionKey || '')
  return (
    k === 'nike-mpp-fww-reset-application-workflow-non-key' ||
    k === 'nike-mpp-fww-design-construction'
  )
})

const isDCFlow = computed(() => {
  const k = String(doorDetail.value?.processDefinitionKey || '')
  const dcKeys = approvalsTab.find((t) => t.key === 'D&C')?.processDefinitionKeys || []
  return dcKeys.includes(k)
})

const isCM2 = computed(() => currentTaskName.value.startsWith('CM2'))
const isGateN1dian1 = computed(() => currentTaskName.value.startsWith('N1.1'))
const isGateN1dian2 = computed(() => currentTaskName.value.startsWith('N1.2'))

const showClosureEdit = ref(false)
const closureAction = ref<'complete' | 'hold' | ''>('')
const closureBusinessId = ref('')
const closureMpComment = ref('')
const closureForm = ref<{
  closureDate: string
  closureReasonId: string
  closureReasonText: string
  aeComment: string
}>({ closureDate: '', closureReasonId: '', closureReasonText: '', aeComment: '' })
const closureSelectVisible = ref(false)
const closureReasonOptions = ref<{ text: string; value: string }[]>([])

const isApproveClosure = computed(() => closureAction.value === 'complete')
const isPendingClosure = computed(() => closureAction.value === 'hold')
const isClosureReasonRequired = computed(() => isApproveClosure.value)
const isAECommentRequired = computed(() => {
  const rid = String(closureForm.value.closureReasonId || '').trim()
  return isPendingClosure.value || (isApproveClosure.value && rid === '501') // 501: Other Reasons
})

const toDateInputValue = (v: any) => {
  if (!v) return ''
  const d = new Date(v)
  if (isNaN(d.getTime())) {
    const str = String(v)
    const parts = str.includes('/') ? str.split('/') : str.split('-')
    if (parts.length === 3) {
      const y = parts[0]
      // @ts-ignore
      const m = parts[1].padStart(2, '0')
      // @ts-ignore
      const d2 = parts[2].padStart(2, '0')
      return `${y}-${m}-${d2}`
    }
    return ''
  }
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const dd = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${dd}`
}

const toTimestamp = (v: any): number | undefined => {
  if (!v && v !== 0) return undefined
  if (typeof v === 'number') return v
  const str = String(v)
  if (/^\d{4}-\d{2}-\d{2}$/.test(str)) {
    const [y, m, d] = str.split('-')
    const dt = new Date(Number(y), Number(m) - 1, Number(d), 0, 0, 0)
    return dt.getTime()
  }
  if (/^\d{4}\/\d{2}\/\d{2}$/.test(str)) {
    const [y, m, d] = str.split('/')
    const dt = new Date(Number(y), Number(m) - 1, Number(d), 0, 0, 0)
    return dt.getTime()
  }
  const dt = new Date(str)
  return isNaN(dt.getTime()) ? undefined : dt.getTime()
}

const openClosureSelect = () => {
  closureSelectVisible.value = true
}

const onClosureSelectConfirm = ({ selectedValues, selectedOptions }: any) => {
  closureSelectVisible.value = false
  const val = String(selectedValues?.[0] || '')
  closureForm.value.closureReasonId = val
  const opt = selectedOptions?.[0]
  closureForm.value.closureReasonText = String(opt?.text || '')
}

// 在调用 doTask 前统一做评论校验（approve/complete 不校验；Closure 流程不校验）
const ensureCommentsBeforeDoTask = async (action: string): Promise<boolean> => {
  if (action === 'complete' || isApprovalsClosure.value) return true
  try {
    const commentType =
      doorDetail.value?.processDefinitionKey === 'nike-mpp-catalyze-non-key-door-approval' &&
      doorDetail.value?.taskDefinitionKey === 'ro-approve'
        ? 'c'
        : 'undefined'
    const res = await getComments(clcDoorCode.value, batchId.value, taskId.value, commentType)
    const commentTypeId = String(res?.data?.commentTypeId || '').trim()
    const commentText = String(res?.data?.comment || '').trim()
    if (!commentTypeId && !commentText) {
      return false
    }
    return true
  } catch (e) {
    console.error(e)
    return false
  }
}

const doTaskWithCheck = async (action: string, ids: string[], payload?: any) => {
  const ok = await ensureCommentsBeforeDoTask(action)
  if (!ok) {
    // 用 next tick 弹 toast，避免与外层 withLoading 的 loading 抢 Vant 单例
    showToastNextTick('No comments')
    active.value = 'comment'
    router.replace({ path: '/approvalsDetail', query: { ...(route.query as any), tab: 'comment' } })
    return false
  }
  try {
    return await doTask(action, ids, payload)
  } catch (error: any) {
    const code = Number(error?.code)
    // 203：附件缺失
    if (code === 203) {
      active.value = 'attachment'
      router.replace({
        path: '/approvalsDetail',
        query: { ...(route.query as any), tab: 'attachment' },
      })
      return false
    }
    // 10021：评论缺失
    if (code === 10021) {
      active.value = 'comment'
      router.replace({
        path: '/approvalsDetail',
        query: { ...(route.query as any), tab: 'comment' },
      })
      return false
    }
    // 401：通用异常 9003：流程异常
    if (code === 401 || code === 9003) {
      return false
    }
    active.value = 'detail'
    router.replace({ path: '/approvalsDetail', query: { ...(route.query as any), tab: 'detail' } })
    detailRef.value?.openEdit?.()
    return false
  }
}

const doTaskClosureWithHandle = async (
  action: 'complete' | 'hold' | 'reject' | 'return',
  data: any,
) => {
  try {
    return await doTaskClosure(action, data)
  } catch (error: any) {
    const code = Number(error?.code)
    if (code === 203) {
      active.value = 'attachment'
      router.replace({
        path: '/approvalsDetail',
        query: { ...(route.query as any), tab: 'attachment' },
      })
      return false
    }
    if (code === 10021) {
      active.value = 'comment'
      router.replace({
        path: '/approvalsDetail',
        query: { ...(route.query as any), tab: 'comment' },
      })
      return false
    }
    if (code === 401 || code === 9003) {
      return false
    }
    active.value = 'detail'
    router.replace({ path: '/approvalsDetail', query: { ...(route.query as any), tab: 'detail' } })
    detailRef.value?.openEdit?.()
    return false
  }
}

const computeReturnTargets = (): string[] => {
  const gate = gateKey.value

  // Default
  let targets: string[] = ['last']

  if (isApprovalsClosure.value) {
    return targets
  }

  // J7/K7/K8/K9 -> K6
  if (['J7', 'K7', 'K8', 'K9'].includes(gate)) {
    targets = ['K6']
  }
  // N5 with FWW -> N1.1
  else if (gate === 'N5' && isFWW.value) {
    targets = ['N1.1']
  }
  // N7/J9/N8/N10.1 -> N6
  else if (['N7', 'J9', 'N8', 'N9.1', 'N10.1'].includes(gate)) {
    targets = ['N6']
  }
  // D1 -> NON-KEY:N6, KEY:K3
  else if (gate === 'D1') {
    targets = ['N6'] // 如果format是key，传N6会自动return到K3
  }
  // D2/D3/D4 -> NON-KEY: Last + N6, KEY: Last + K3
  else if (['D2', 'D3', 'D4'].includes(gate)) {
    targets = ['last', 'N6'] // 如果format是key，传N6会自动return到K3
  }
  // 当fww时，不存在D2节点，隐藏last
  if (isFWW.value && gate === 'D3') {
    targets = targets.filter((t) => t !== 'last')
  }
  return targets
}

const returnTargets = computed(() => computeReturnTargets())
const returnConfirm1Text = computed(() => {
  const t = returnTargets.value[0]
  return t ? getReturnTargetText(t) : ''
})
const returnConfirm2Text = computed(() => {
  const t = returnTargets.value[1]
  return t ? getReturnTargetText(t) : ''
})
const onReturnConfirm1 = () => {
  const t = returnTargets.value[0]
  if (t) onReturnTarget(t)
}
const onReturnConfirm2 = () => {
  const t = returnTargets.value[1]
  if (t) onReturnTarget(t)
}

const onReturnTarget = async (target: string) => {
  if (!taskId.value) {
    returnDialogVisible.value = false
    return
  }
  let ok = true
  try {
    await withLoading(
      async () => {
        if (isApprovalsClosure.value) {
          const payload: any = {
            id: closureBusinessId.value || '',
            taskId: taskId.value,
            taskDefinitionKey: String(doorDetail.value?.taskDefinitionKey || ''),
            processDefinitionKey: String(doorDetail.value?.processDefinitionKey || ''),
            mpComment: closureMpComment.value,
          }
          const res = await doTaskClosureWithHandle('return', payload)
          if (res === false) ok = false
        } else {
          const res = await doTaskWithCheck('return', [taskId.value], { returnTarget: target })
          if (res === false) ok = false
        }
      },
      { message: 'Processing...' },
    )
    if (ok) {
      showToastNextTick('Success')
      setTimeout(() => {
        router.back()
      }, 1000)
    }
  } catch (error) {
    console.error(error)
  } finally {
    returnDialogVisible.value = false
  }
}

const getReturnTargetText = (t: string) => {
  if (t === 'last') {
    const key = gateKey.value
    let mapped = lastGateConfig[key]
    if (isFWW.value && key === 'C4') mapped = 'C0'
    return mapped ? `Return To ${mapped}` : 'Last Gate'
  }
  const to = t === 'N6' && currentTaskName.value.startsWith('D') ? 'N6/K3' : t
  return `Return To ${to}`
}

const handleAction = async (type: ApprovalAction) => {
  if (
    [
      ApprovalAction.Reject,
      ApprovalAction.Return,
      ApprovalAction.Drop,
      ApprovalAction.Pending,
      ApprovalAction.Backup,
    ].includes(type)
  ) {
    await commentRef.value?.saveComments?.()
  }
  if (isCM2.value && (type === ApprovalAction.Approve || type === ApprovalAction.Pending)) {
    closureAction.value = type === ApprovalAction.Approve ? 'complete' : 'hold'
    await prepareClosureEdit()
    showClosureEdit.value = true
    return
  }
  
  if (type === ApprovalAction.Approve) {
    const taskName = currentTaskName.value || ''
    const hasN6 = taskName.startsWith('N6')
    const hasK3 = taskName.startsWith('K3')
    if ((hasN6 || hasK3) && !doorDetail.value?.franchise) {
      active.value = 'detail'
      router.replace({ path: '/approvalsDetail', query: { ...(route.query as any), tab: 'detail' } })
      showToast('Franchise is required')
      setTimeout(() => {
        detailRef.value?.openEdit?.()
      }, 400)
      return
    }
  }
  
  const requiredTypes =
    !isApprovalsClosure.value && type === ApprovalAction.Approve
      ? requiredAttachmentConfig[gateKey.value] || []
      : []
  const projectTypeMain = String(doorDetail.value?.projectTypeMain || '').toUpperCase()
  const projectType = String(doorDetail.value?.projectType || '').trim()
  const floorVal = String(doorDetail.value?.floor || '')
    .trim()
    .toUpperCase()
  // 根据 projectTypeMain 和 floor进行额外的必传校验
  const extraRequired: string[] = []
  if (type === ApprovalAction.Approve && !isDCFlow.value) {
    if (projectTypeMain === 'NEW') {
      extraRequired.push('mix_for_new_door')
    } else if (projectTypeMain === 'RENO') {
      const isRezoning = ['Top Door', 'FWW', 'Change Order'].includes(projectType)
      // RenoN1.1和N1.2必传before和after，Rezoning不必传
      if (!isRezoning && (isGateN1dian1.value || isGateN1dian2.value)) {
        extraRequired.push('mix_for_reno_door_before', 'mix_for_reno_door_after')
      }
    }
    if (floorVal && floorVal !== '1F') {
      extraRequired.push('mix_1st_reno_floor')
    }
  }
  let allRequired =
    type === ApprovalAction.Approve ? Array.from(new Set([...requiredTypes, ...extraRequired])) : []
  if (!isApprovalsClosure.value && type === ApprovalAction.Approve && allRequired.length > 0) {
    try {
      const res = await getAttachments(batchId.value, { loading: 'Validating...' })
      const uploaded = res.data || []
      const uploadedTypes = new Set(uploaded.map((a: any) => a.docType))
      const missing = allRequired.filter((t) => !uploadedTypes.has(t))

      if (missing.length > 0) {
        const missingNames = missing.map((m) => {
          const found = docTypeList.find((d) => d.name === m)
          return found ? found.value : m
        })
        showToast(`Missing required attachments: ${missingNames.join(', ')}`)
        active.value = 'attachment'
        router.replace({
          path: '/approvalsDetail',
          query: { ...(route.query as any), tab: 'attachment' },
        })
        return
      }
    } catch (error) {
      console.error(error)
      return
    }
  }

  if (type === ApprovalAction.Return) {
    returnDialogVisible.value = true
    return
  }
  confirmDialogAction.value = type
  confirmDialogVisible.value = true
}

const onConfirmAction = async () => {
  const type = confirmDialogAction.value
  confirmDialogVisible.value = false
  if (!taskId.value) return

  // 提前做本地校验和 action 映射，不占用 loading 生命周期
  if (type === ApprovalAction.Pending && !clcDoorCode.value) return

  const specialKeys = ['nike-mpp-design-construction', 'nike-mpp-fww-design-construction']
  const isSpecialDrop =
    type === ApprovalAction.Drop &&
    specialKeys.includes(String(doorDetail.value?.processDefinitionKey || ''))
  if (isSpecialDrop && !clcProjectCode.value) return

  let action = ''
  switch (type) {
    case ApprovalAction.Approve:
      action = 'complete'
      break
    case ApprovalAction.Reject:
      action = 'reject'
      break
    case ApprovalAction.Backup:
      action = 'return'
      break
  }
  if (!action && type !== ApprovalAction.Pending && type !== ApprovalAction.Drop) return

  let ok = true
  try {
    await withLoading(
      async () => {
        if (type === ApprovalAction.Pending) {
          const res = await pendingTaskWithHandle([
            { doorCode: clcDoorCode.value, taskId: taskId.value },
          ])
          if (res === false) ok = false
        } else if (type === ApprovalAction.Drop) {
          if (isSpecialDrop) {
            const res = await dropTaskWithHandle([clcProjectCode.value])
            if (res === false) ok = false
          } else {
            const res = await doTaskWithCheck('drop', [taskId.value])
            if (res === false) ok = false
          }
        } else if (isApprovalsClosure.value) {
          const payload: any = {
            id: closureBusinessId.value || '',
            taskId: taskId.value,
            taskDefinitionKey: String(doorDetail.value?.taskDefinitionKey || ''),
            processDefinitionKey: String(doorDetail.value?.processDefinitionKey || ''),
          }
          const res = await doTaskClosureWithHandle(action as any, payload)
          if (res === false) ok = false
        } else {
          const res = await doTaskWithCheck(action, [taskId.value])
          if (res === false) ok = false
        }
      },
      { message: 'Processing...' },
    )
    if (ok) {
      showToastNextTick('Success')
      setTimeout(() => {
        router.back()
      }, 1000)
    }
  } catch (error) {
    console.error(error)
  }
}

const fetchDetail = async () => {
  if (!clcDoorCode.value) return
  loading.value = true
  try {
    let res: any
    // 在door summary页面的部分类型下卡片需要调用不同的接口
    if (isDoorSummaryView.value && [0, 3, 4, 5, 6, 7].includes(indexFromDoorSummary.value)) {
      res = await getDoorObserverClcDoor(clcDoorCode.value)
    } else {
      if (!taskId.value) return
      res = await getClcDoor(clcDoorCode.value, taskId.value)
    }
    doorDetail.value = {
      ...doorDetail.value,
      ...res.data,
      storeNameLocal: storeNameLocal.value,
      clcProjectCode: clcProjectCode.value,
    }
  } catch (e) {
    console.error(e)
  } finally {
    loading.value = false
  }
}

onMounted(() => {
  userName.value = sessionStorage.getItem('user_name') || ''
  fetchDetail()
})

const prepareClosureEdit = async () => {
  try {
    const res = await getClosureRuleOptions('Closure rules')
    const list = Array.isArray(res?.data) ? res.data : []
    closureReasonOptions.value = list.map((it: any) => ({
      text: String(it?.typeName || ''),
      value: String(it?.id || ''),
    }))
  } catch (e) {}
  try {
    const todoRes = await getClosureTodo(
      'nike-mpp-closure-door-mp-approval',
      'terri-acct-review-closure-cm',
    )
    const arr = Array.isArray(todoRes?.data) ? todoRes.data : []
    const found = arr.find((it: any) => String(it?.id || '') === taskId.value)
    const biz = found?.business || {}
    closureBusinessId.value = String(biz?.id || '')
    closureMpComment.value = String(biz?.mpComment || '')
    closureForm.value.aeComment = String(biz?.aeComment || '')
    closureForm.value.closureDate = toDateInputValue(biz?.closureDate || '')
    const rid = String(biz?.closureReason || '')
    closureForm.value.closureReasonId = rid
    const opt = closureReasonOptions.value.find((o) => String(o.value) === rid)
    closureForm.value.closureReasonText = opt ? opt.text : ''
  } catch (e) {}
}

const validateClosureRequired = (): string[] => {
  const errs: string[] = []
  if (isClosureReasonRequired.value) {
    const rid = String(closureForm.value.closureReasonId || '').trim()
    if (!rid) errs.push('Closure Reason')
  }
  if (isAECommentRequired.value) {
    const comment = String(closureForm.value.aeComment || '').trim()
    if (!comment) errs.push('AE Comment')
  }
  return errs
}

const onClosureConfirm = async () => {
  const errs = validateClosureRequired()
  if (errs.length) {
    showToast(`Please complete required fields: ${errs.join(', ')}`)
    return
  }
  const payload: any = {
    id: closureBusinessId.value,
    taskId: taskId.value,
    taskDefinitionKey: String(doorDetail.value?.taskDefinitionKey || ''),
    processDefinitionKey: String(doorDetail.value?.processDefinitionKey || ''),
    closureDate: toTimestamp(closureForm.value.closureDate),
    closureReason: Number(closureForm.value.closureReasonId),
    aeComment: closureForm.value.aeComment,
  }
  let ok = true
  try {
    await withLoading(
      async () => {
        const res = await doTaskClosureWithHandle(
          closureAction.value as 'complete' | 'hold',
          payload,
        )
        if (res === false) ok = false
      },
      { message: 'Processing...' },
    )
    if (ok) {
      showToastNextTick('Success')
      showClosureEdit.value = false
      setTimeout(() => {
        router.back()
      }, 1000)
    }
  } catch (error) {
    console.error(error)
  }
}
</script>

<style scoped lang="scss">
.detail-container {
  width: 100%;
  height: 100vh;
  display: flex;
  flex-direction: column;
  background-color: #f7f8fa;
}
.detail-header {
  background-color: #fff;
  flex-shrink: 0;
}
.user-name {
  margin-left: auto;
  font-size: 12px;
  color: #333;
}
.detail-title {
  font-size: 16px;
  font-weight: bold;
}
.content-wrapper {
  flex: 1;
  overflow-y: auto;
  background-color: #fff;
  padding-bottom: calc(60px + env(safe-area-inset-bottom));
}
.content-wrapper :deep(.van-tabs__wrap) {
  position: sticky;
  top: 0;
  z-index: 8;
  background-color: #fff;
}
.footer-actions {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 9;
  background-color: #fff;
  border-top: 1px solid #ebedf0;
  padding-bottom: max(8px, env(safe-area-inset-bottom));
}
@media (orientation: landscape) {
  .detail-header,
  .content-wrapper :deep(.van-tabs__wrap) {
    zoom: 0.7;
  }
  .footer-actions {
    zoom: 0.5;
  }
}
.return-target-btn {
  min-width: 60px;
}
.aligned-dialog :global(.van-dialog__header) {
  padding-top: 15px;
  justify-content: center;
}
.aligned-dialog :global(.van-dialog__header *) {
  font-weight: bold;
}
:global(.aligned-dialog) {
  font-family: var(--van-base-font);
}
.aligned-dialog :global(.van-dialog__title),
.aligned-dialog :global(.van-dialog__message) {
  font-family: var(--van-base-font);
}
.aligned-dialog :global(.van-dialog__content) {
  font-size: var(--van-dialog-message-font-size);
}
.dialog-title {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 0 14px;
}
.title-icon {
  width: 16px;
  height: 16px;
}
.more-btn {
  border: none;
  width: 50px;
  min-width: 50px;
  padding: 4px 6px;
}
.more-content {
  display: flex;
  flex-direction: column;
  align-items: center;
}
.more-icon {
  width: 20px;
  height: 20px;
  filter: brightness(0) invert(1);
}
.more-text {
  color: #fff;
  font-size: 14px;
  line-height: 1;
  padding-bottom: 2px;
}
.edit-form {
  padding: 4px 8px;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px 12px;
}
.edit-row {
  margin-bottom: 0;
}
.edit-label {
  font-size: 12px;
  color: #7f7f7f;
  margin-bottom: 4px;
}
.edit-input {
  width: 100%;
  border: 1px solid #ebedf0;
  border-radius: 6px;
  padding: 6px 8px;
  font-size: 13px;
}
.edit-textarea {
  width: 100%;
  min-height: 72px;
  border: 1px solid #ebedf0;
  border-radius: 6px;
  padding: 6px 8px;
  font-size: 13px;
  resize: none;
}
.col-span-2 {
  grid-column: span 2;
}

.dialog-footer-fixed {
  position: sticky;
  bottom: 0;
  left: 0;
  right: 0;
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding: 8px;
  background: #fff;
  border-top: 1px solid #ebedf0;
}
.dialog-confirm,
.dialog-cancel {
  height: 40px;
  min-width: 80px;
}
.dialog-confirm {
  background-color: #6e010f;
  border: none;
}
.req {
  color: #ee0a24;
  margin-right: 2px;
}
</style>
